import React, { useMemo } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Requisition } from '../../types';
import Select from './Select';
import TextArea from './TextArea';
import { useEmployees } from '../../hooks/useEmployees';

interface RequisitionStatusSectionProps {
  register: UseFormRegister<Requisition>;
  errors: FieldErrors<Requisition>;
}

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'purchased', label: 'Purchased' },
  { value: 'delivery-noted', label: 'Delivery Noted' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' }
];

const RequisitionStatusSection: React.FC<RequisitionStatusSectionProps> = ({ register, errors }) => {
  const { data: employees = [] } = useEmployees();
  const employeeOptions = useMemo(() =>
    (employees as any[])
      .filter((e: any) => e.name)
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
      .map((e: any) => ({ value: e.name, label: e.name })),
    [employees]);

  return (
    <>
      {/* Approvals */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Approvals</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Select
                label="Prepared By"
                {...register('preparedBy', { required: 'Prepared by is required' })}
                error={errors.preparedBy?.message}
                options={employeeOptions}
                placeholder="Select employee..."
              />
            </div>
            <div>
              <Select
                label="Chief Officer Name"
                {...register('chiefOfficerName')}
                options={employeeOptions}
                placeholder="Select employee..."
              />
            </div>
            <div>
              <Select
                label="Chief Engineer Name"
                {...register('captainName')}
                options={employeeOptions}
                placeholder="Select employee..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status and Notes */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Status & Notes</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Select
                label="Status"
                {...register('status', { required: 'Status is required' })}
                options={statusOptions}
                error={errors.status?.message}
              />
            </div>
            <div>
              <Select
                label="Approved By Manager"
                {...register('approvedByManager')}
                options={employeeOptions}
                placeholder="Select employee (if approved)..."
              />
            </div>
            <div className="md:col-span-2">
              <TextArea
                label="Additional Notes"
                {...register('notes')}
                rows={3}
                placeholder="Any additional notes or special instructions..."
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequisitionStatusSection;
